import { useEffect, useMemo, useRef, useState } from "react";
import { LinearProgress, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import type { BookingInputs } from "../../FacilityManager/Domain/Types/BookingInputs.type";
import type { IFloorPayload } from "../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { BookingServicePromptDialogContents } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookingServicePromptDialogContents";
import type { BookingServiceMapConfig } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookingServicePromptDialog";
import {
  BookingServiceEditedData,
  ServiceOrderDto
} from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/Domain/BookingServicePrompt.types";
import {
  handleAssignedBookableEquipItems,
  handleAssignedCateringItems,
  handleAssignedRoomLayItems
} from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/handleAssignedServiceItems.function";
import { mapBookEquipmentTableData } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookableEquipment/BookingService-BookableEquipment.functions";
import { mapCateringEditedData } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/Catering/BookingService-CateringTable.functions";
import { mapRoomLayoutTableData } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/RoomLayout/BookingService-RoomLayoutTable.functions";
import { isMandatoryConferenceServiceIncomplete } from "../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/hasMandatory.function";
import { getPlanDateRangeOrNull } from "../../../features/Booking-Form/functions/floor.functions";
import { useRemoteMutateFetchFloorPlanService } from "../../../hooks/Remote/FloorPlan/useRemoteMutateFetchFloorPlanService";
import { useRemoteFetchAllCateringServices } from "../../../hooks/Remote/Services/CateringMenu/useRemoteFetchAllCateringServices";
import { useRemoteFetchBookableEquipments } from "../../../hooks/Remote/BookableEquipment/useRemoteFetchBookableEquipment";
import { useRemoteFetchRoomlayout } from "../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";
import { useRemoteFetchMaxSetupTime } from "../../../hooks/Remote/Services/BookingServices/useRemoteFetchMaxSetupTime";
import { useRemoteFetchPermissionContext } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchPermissionContext";
import { useIsUserServiceAdminForLocation } from "../../../hooks/Remote/Admin/useIsUserServiceAdminForLocation";
import type { ChangeConferenceRoomSelection } from "./change-conference-room.types";
import {
  buildSyntheticBookingInputsFromSchedule,
  canProceedFromChangeRoomServicesStep,
  resolveLocationInventoryIdFromSchedule
} from "./change-conference-room.functions";

const emptyEditedService: BookingServiceEditedData = {
  cateringData: [],
  bookableEquipmentData: [],
  roomLayoutData: []
};

const emptyOrderedService: ServiceOrderDto = {
  caterings: [],
  costCenterId: null,
  bookableEquipments: [],
  roomLayout: []
};

type P = {
  schedule: BookingScheduleInterface;
  selection: ChangeConferenceRoomSelection;
  onOrderChange: (order: ServiceOrderDto) => void;
  onStepStateChange?: (state: {
    loading: boolean;
    hasConfigurableTabs: boolean;
    canProceed: boolean;
  }) => void;
};

export function ChangeConferenceRoomServicesStep({
  schedule,
  selection,
  onOrderChange,
  onStepStateChange
}: Readonly<P>) {
  const { t } = useTranslation();
  const { sub: userId } = useSelector((state: RootState) => state.login.userInformation);
  const isRequestAdmin = useSelector((state: RootState) => state.login.userRoles.isRequestAdmin);

  const locationInventoryId = resolveLocationInventoryIdFromSchedule(schedule) ?? 0;
  const startIso = schedule.start ?? schedule.startDate;
  const endIso = schedule.end ?? schedule.endDate;
  const startDt = DateTime.fromISO(startIso);
  const endDt = DateTime.fromISO(endIso);
  const bookingFrom = startDt.toFormat("yyyy-MM-dd");
  const bookingTo = endDt.toFormat("yyyy-MM-dd");
  const chosenTimes = {
    startTime: startDt.toFormat("HH:mm"),
    endTime: endDt.toFormat("HH:mm")
  };

  const bookingInputs = useMemo(
    () => buildSyntheticBookingInputsFromSchedule(schedule),
    [schedule]
  );

  const bookingInputsForService = useMemo(
    () =>
      ({
        ...bookingInputs,
        bookingFrom,
        bookingTo,
        bookingStart: chosenTimes.startTime,
        bookingEnd: chosenTimes.endTime
      }) as BookingInputs,
    [bookingFrom, bookingInputs, bookingTo, chosenTimes.endTime, chosenTimes.startTime]
  );

  const [floorPlanForService, setFloorPlanForService] = useState<IFloorPayload | undefined>();
  const [serviceDataLoading, setServiceDataLoading] = useState(false);
  const [editedService, setEditedService] = useState<BookingServiceEditedData>(emptyEditedService);
  const [orderedService, setOrderedService] = useState<ServiceOrderDto>(emptyOrderedService);
  const [selectTab, setSelectTab] = useState(0);
  const serviceInitKeyRef = useRef("");
  const servicesTabsInitializedRef = useRef(false);

  const { mutateAsync: fetchFloorPlanForService } = useRemoteMutateFetchFloorPlanService();
  const { data: remoteCateringMenu } = useRemoteFetchAllCateringServices(locationInventoryId);
  const { data: remoteBookableEquipment } = useRemoteFetchBookableEquipments();
  const { data: remoteRoomLayout } = useRemoteFetchRoomlayout();
  const { data: permissionContextData } = useRemoteFetchPermissionContext(true);
  const permissionContext = permissionContextData?.[0];

  const { data: isServiceAdminForLocation, isServiceAdminLookupPending } =
    useIsUserServiceAdminForLocation(locationInventoryId, userId, isRequestAdmin);
  const isServiceAdminBypass = isServiceAdminForLocation === true;

  const { data: remoteMaxSetupTime } = useRemoteFetchMaxSetupTime(
    {
      bookingInventoryIds: [selection.newBookingInventoryId],
      startDate: bookingFrom,
      endDate: bookingTo,
      startTime: chosenTimes.startTime,
      endTime: chosenTimes.endTime,
      bookingType: BookingType.CONFERENCEZONE,
      timezone: schedule.timezone,
      weekdays: bookingInputs.weekdays,
      monday: "no use"
    },
    Boolean(bookingFrom && bookingTo)
  );

  const selectedZoneScheduleId = selection.newZoneScheduleId;

  useEffect(() => {
    const planRange = getPlanDateRangeOrNull(
      bookingFrom,
      chosenTimes.startTime,
      bookingTo,
      chosenTimes.endTime
    );
    if (!planRange) {
      setFloorPlanForService(undefined);
      return;
    }

    let cancelled = false;
    setServiceDataLoading(true);

    fetchFloorPlanForService({
      floorInventoryId: selection.newFloorInventoryId,
      dto: { end: planRange.end, start: planRange.start }
    })
      .then(data => {
        if (!cancelled) {
          setFloorPlanForService(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFloorPlanForService(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setServiceDataLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    bookingFrom,
    bookingTo,
    chosenTimes.endTime,
    chosenTimes.startTime,
    fetchFloorPlanForService,
    selection.newFloorInventoryId
  ]);

  useEffect(() => {
    if (!floorPlanForService || !remoteMaxSetupTime || isServiceAdminLookupPending) {
      return;
    }
    if (
      !remoteCateringMenu ||
      !remoteBookableEquipment ||
      !remoteRoomLayout ||
      !permissionContext
    ) {
      return;
    }

    const initKey = [
      selection.newBookingInventoryId,
      selectedZoneScheduleId,
      bookingFrom,
      bookingTo,
      chosenTimes.startTime,
      chosenTimes.endTime
    ].join("|");
    if (serviceInitKeyRef.current === initKey) {
      return;
    }
    serviceInitKeyRef.current = initKey;
    servicesTabsInitializedRef.current = false;

    const assignedCateringItems = handleAssignedCateringItems({
      floorPlanForService,
      selectedZone: selectedZoneScheduleId,
      remoteCateringMenu,
      permissionContext,
      userId
    });
    const assignedBookableEquipItems = handleAssignedBookableEquipItems({
      floorPlanForService,
      selectedZone: selectedZoneScheduleId,
      remoteBookableEquipment
    });
    const assignedRoomLayoutItems = handleAssignedRoomLayItems({
      floorPlanForService,
      selectedZone: selectedZoneScheduleId,
      remoteRoomLayout: remoteRoomLayout.map(item => ({ ...item, arrangement: item.arrangement }))
    });

    setEditedService({
      cateringData: mapCateringEditedData(
        bookingInputsForService,
        assignedCateringItems,
        undefined,
        remoteMaxSetupTime.maxSetUpTime,
        isServiceAdminBypass,
        true
      ),
      bookableEquipmentData: mapBookEquipmentTableData(
        bookingInputsForService,
        assignedBookableEquipItems as Parameters<typeof mapBookEquipmentTableData>[1],
        remoteMaxSetupTime.maxSetUpTime,
        isServiceAdminBypass
      ),
      roomLayoutData: mapRoomLayoutTableData(
        bookingInputsForService,
        assignedRoomLayoutItems as Parameters<typeof mapRoomLayoutTableData>[1],
        remoteMaxSetupTime.maxSetUpTime,
        isServiceAdminBypass
      )
    });

    setOrderedService(emptyOrderedService);
  }, [
    bookingFrom,
    bookingInputsForService,
    bookingTo,
    chosenTimes.endTime,
    chosenTimes.startTime,
    floorPlanForService,
    isServiceAdminBypass,
    isServiceAdminLookupPending,
    permissionContext,
    remoteBookableEquipment,
    remoteCateringMenu,
    remoteMaxSetupTime,
    remoteRoomLayout,
    selectedZoneScheduleId,
    selection.newBookingInventoryId,
    userId
  ]);

  const servicesSectionLoading =
    serviceDataLoading ||
    !remoteMaxSetupTime ||
    !remoteCateringMenu ||
    !remoteBookableEquipment ||
    !remoteRoomLayout ||
    !permissionContextData ||
    isServiceAdminLookupPending;

  const mappedTabs: BookingServiceMapConfig[] = useMemo(() => {
    const tabConfigs: BookingServiceMapConfig[] = [
      {
        key: "catering",
        label: "Catering",
        editedData: editedService.cateringData,
        usedItems: orderedService.caterings.filter(c => c.qty > 0).length,
        isMandatory: editedService.cateringData.some(item => item.selectingIsMandatory === true),
        testId: "change-room-services-tab-catering",
        originalIndex: 0
      },
      {
        key: "equipment",
        label: "Equipment",
        editedData: editedService.bookableEquipmentData,
        usedItems: orderedService.bookableEquipments.length,
        isMandatory: editedService.bookableEquipmentData.some(
          item => item.selectingIsMandatory === true
        ),
        testId: "change-room-services-tab-equipment",
        originalIndex: 1
      },
      {
        key: "roomLayout",
        label: "Room Layout",
        editedData: editedService.roomLayoutData,
        usedItems: orderedService.roomLayout.length,
        isMandatory: editedService.roomLayoutData.some(item => item.selectingIsMandatory === true),
        testId: "change-room-services-tab-layout",
        originalIndex: 2
      }
    ];

    const visibleTabs = tabConfigs.filter(tab => tab.editedData.length > 0 || tab.usedItems > 0);
    return visibleTabs.map((tab, index) => ({ ...tab, mappedIndex: index }));
  }, [editedService, orderedService]);

  useEffect(() => {
    if (mappedTabs.length === 0) {
      setSelectTab(0);
      return;
    }
    if (!servicesTabsInitializedRef.current) {
      const firstMandatory = mappedTabs.find(tab => tab.isMandatory);
      setSelectTab(firstMandatory?.mappedIndex ?? mappedTabs[0].mappedIndex ?? 0);
      servicesTabsInitializedRef.current = true;
      return;
    }
    const hasCurrentTab = mappedTabs.some(tab => tab.mappedIndex === selectTab);
    if (!hasCurrentTab) {
      setSelectTab(mappedTabs[0].mappedIndex ?? 0);
    }
  }, [mappedTabs, selectTab]);

  const sanitizedOrder = useMemo(
    () => ({
      ...orderedService,
      caterings: orderedService.caterings.filter(item => item.qty > 0)
    }),
    [orderedService]
  );

  useEffect(() => {
    onOrderChange(sanitizedOrder);
  }, [onOrderChange, sanitizedOrder]);

  const hasIncompleteMandatory = useMemo(() => {
    if (servicesSectionLoading) {
      return true;
    }
    return isMandatoryConferenceServiceIncomplete({
      isConferenceZoneBooking: true,
      floorPlanForService,
      selectedZone: selectedZoneScheduleId,
      conferenceServiceInventoryKey: selection.newBookingInventoryId,
      conferenceService: {
        [selection.newBookingInventoryId]: sanitizedOrder
      }
    });
  }, [
    floorPlanForService,
    sanitizedOrder,
    selectedZoneScheduleId,
    selection.newBookingInventoryId,
    servicesSectionLoading
  ]);

  useEffect(() => {
    const canProceed = canProceedFromChangeRoomServicesStep({
      servicesSectionLoading,
      configurableServiceTabCount: mappedTabs.length,
      hasIncompleteMandatory
    });
    onStepStateChange?.({
      loading: servicesSectionLoading,
      hasConfigurableTabs: mappedTabs.length > 0,
      canProceed
    });
  }, [hasIncompleteMandatory, mappedTabs.length, onStepStateChange, servicesSectionLoading]);

  if (servicesSectionLoading) {
    return (
      <Stack spacing={1} data-testid="change-room-services-loading">
        <LinearProgress />
        <Typography variant="body2" color="text.secondary">
          {t("_changeRoom_servicesLoading")}
        </Typography>
      </Stack>
    );
  }

  if (mappedTabs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="change-room-services-empty">
        {t("_changeRoom_servicesNone")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1} data-testid="change-room-services-step">
      <Typography variant="subtitle2">{t("_changeRoom_servicesStepTitle")}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t("_changeRoom_servicesStepHint")}
      </Typography>
      <BookingServicePromptDialogContents
        mappedTabs={mappedTabs}
        editedService={editedService}
        setEditedService={setEditedService}
        orderedService={orderedService}
        setOrderedService={setOrderedService}
        selectTab={selectTab}
        setSelectTab={setSelectTab}
      />
    </Stack>
  );
}
